import _ = require('underscore')

import {XmlTagInfo} from "../xmlTagProvider";
import Grid from "./grid";
import {QuestionaryException} from "../../exceptions";
import {ValidateResult} from "../contentType";

import {CellItem} from "../items/cellItem";
import {CheckboxItem} from "../items/checkbox";
import {ImageItem} from "../items/imageItem";
import {InputTextItem} from "../items/inputText";
import {LabelItem} from "../items/labelItem";
import {RadioItem} from "../items/radio";
import {SliderItem} from "../items/slider";
import {SyntheticItem} from "../items/synthetic";
import {TextareaItem} from "../items/textarea";
import {TextboxItem} from "../items/textbox";
import {VideoItem} from "../items/video";
import {ComboItem} from "../items/combo";
import {DragSourceItem} from "../items/dragdrop/dragSourceItem";
import {DropTargetItem} from "../items/dragdrop/dropTargetItem";
import {ScannerItem} from "../items/ScannerItem";

interface CellOptions{
    id: string;
    row: number;
    width: number;
    height: number;
    column: number;
    borders?: any;
    controlDefinitions?: any;
    items?: CellItem []
    textAlign?: string;
}

interface CellSpan {
    width: number;
    height: number;
}

export default class Cell {

    tag :string = 'cell';

    borders:any = null; // cell.borders, todo
    controlDefinitions: XmlTagInfo[] = [];

    valid = true;
    active = false;
    span?: CellSpan;

    cellItems: (CellItem)[] = [];
    private _visible = true;
    private _enabled = true;
    private _cssClass?: string;

    private constructor(
        public id: string,
        private _grid: Grid,
        public row: number,
        public column: number,
        public width: number,
        public height: number,
        public textAlign: string,
        cssClass: string|undefined
    ) {
        this._cssClass = cssClass;
    }

    static fromXml(node: XmlTagInfo, grid: Grid): Cell {
        function isBorders(e:any): boolean {
            return e.tag === "borders";
        }
        // get items that are NOT borders
        const itemDefinitions: any[] = _.reject(node.children, isBorders);

        const cell = new Cell(
            <string> node.attributes['id'],
            grid,
            parseInt(node.attributes['row'], 10),
            parseInt(node.attributes['column'], 10),
            parseInt(node.attributes['width'], 10),
            parseInt(node.attributes['height'], 10),
            node.attributes['textAlign'],
            node.attributes['class']
        );

        cell.controlDefinitions = itemDefinitions || [];
        cell.cellItems = Cell.itemsFromXmlArray(itemDefinitions, grid) || [];
        cell.borders = _.filter(node.children, isBorders)[0];

        return cell;
    }

    static itemsFromXmlArray(nodes: XmlTagInfo[], grid: Grid) {
        const tagMap: {[name:string]:any} = {
            'checkbox' : CheckboxItem,
            'image': ImageItem,
            'inputText': InputTextItem,
            'label': LabelItem,
            'radio': RadioItem,
            'slider': SliderItem,
            'synthetic': SyntheticItem,
            'textarea': TextareaItem,
            'textbox': TextboxItem,
            'video': VideoItem,
            'combo': ComboItem,
            'dragSource': DragSourceItem,
            'dropTarget': DropTargetItem,
            'scanner': ScannerItem,
        };

        return _.map(nodes, (n:XmlTagInfo):CellItem => {
            const cls: any = tagMap[n.tag];
            if (!cls) {
                // TODO log unrecognized CellItem
                // @ts-ignore
                return null;
            }
            const ci: CellItem = cls.fromXml(n);
            ci.initGroup(grid);
            return ci;
        });
    }

    static emptyCell(grid: Grid, column: number, row: number): Cell {
        return new Cell(
        'isEmpty' + column + '_' + row,
            grid,
            row,
            column,
            0,
            0,
            '',
            ''
        );
    }

    public get grid(): Grid {
        return this._grid;
    }

    initControls () {
        _.each(this.cellItems, (c: CellItem) => {
            if(c) {
                c.init(this._grid.questionary, this._grid.form, this)
            }
        });
    }

    updateGroups() {
        this.controlDefinitions!.forEach( (c:XmlTagInfo) => {
            if( 'group' in c.attributes && 'variable' in c.attributes ) {
                const groupId = c.attributes['group'];
                const group = this._grid.groupById(groupId);
                if( !group ) {
                    // console.log(`Group ${groupId} not found`);
                    throw new QuestionaryException(`Group ${groupId} not found`);
                }
                group.addVariableName(c.attributes['variable'])
            }
        })
    }

    get visible() {
        return this._visible;
    }
    set visible(value: boolean) {
        this._visible = value;
        if( !this.visible ) {
            // Reset when hidden
            this.cellItems.forEach(c => c.reset());
        }
    }
    get enabled() {
        return this._enabled;
    }
    set enabled(value: boolean) {
        this._enabled = value;
        if( !this.enabled ) {
            // Reset when disabled
            this.cellItems.forEach(c => c.reset());
        }
    }
    get cssClass() {
        return this._cssClass;
    }
    validate(): ValidateResult {
        if (!this.visible || !this.enabled) {
            this.valid = true;
            return ValidateResult.okResult();
        }

        const result = this.cellItems
            .filter( (c: CellItem) => c.group!.shouldValidate)
            .reduce((acc: ValidateResult, control: CellItem) => acc.createMerged(control.validate()), ValidateResult.okResult());
        this.valid = result.valid;
        return result;
    }

    getItems(): CellItem[]{
        return this.cellItems;
    }

    hasInputText(): boolean {
        return !_.isUndefined(this.getInputText());
    }

    getInputText(): InputTextItem|undefined {
        for (let cellItem of this.cellItems) {
            if (cellItem instanceof InputTextItem) {
                return cellItem;
            }
        }
    }

    isVisible(): boolean {
        return this.visible && this.cellItems.some((c:CellItem) => c.isVisible());
    }

    getColSpan(): number {
        if( !this.span ) {
            return 1;
        } else {
            const hiddenColumns = _.range(this.column!, this.column! + this.span.width)
                .map(c => this._grid.isColumnVisible(c))
                .filter(v => !v)
                .length;
            return this.span.width - hiddenColumns;
        }
    }
    getRowSpan(): number {
        if( !this.span ) {
            return 1;
        } else {
            const hiddenRows = _.range(this.row!, this.row! + this.span.height)
                .map(r => this._grid.isRowVisible(r))
                .filter(v => !v)
                .length;
            return this.span.height - hiddenRows;
        }
    }

}
